import React, {FC, useMemo} from "react";
import useTranslate from "../../../core/languages/hooks/useTranslate";
import useCustomerContext from "../../../customers/hooks/useCustomerContext";
import Badge from "../../../ui/shared/Badge/Badge";
import {TwMainColor} from "../../../ui/data/types";
import moment from "moment";
import {TransferStatusEnum} from "../../enums/TransferStatusEnum";
import {TransferDirectionEnum} from "../../enums/TransferDirectionEnum";
import {formatNumber} from "../../../common/utils/number";
import {
    getTransportDateLabel,
    getTransportTimeLabel,
    getVehicleLabel
} from "../../utils/transportLabels";
import NcImage from "../../../ui/shared/NcImage/NcImage";
import ApiTransfer from "../../types/ApiTransfer";

export interface TransferItemProps {
    item: ApiTransfer;
}

const TransferItem: FC<TransferItemProps> = ({ item }) => {
    const t = useTranslate();
    const {languageCode} = useCustomerContext();

    // Map transfer status to color - Based on TransferStatusEnum values
    const statusColor = useMemo<TwMainColor>(() => {
        switch (item.status) {// Updated - Púrpura
            case TransferStatusEnum.CANCELLED:
                return 'red';     // Cancelled - Rojo
            case TransferStatusEnum.CONFIRMED:
                return 'green';   // Confirmed - Verde
            default:
                return 'yellow';
        }
    }, [item.status]);

    // Get transfer direction label
    const directionLabel = useMemo(() => {
        switch (item.direction) {
            case TransferDirectionEnum.ARRIVAL:
                return t('common.enum.transferDirection.oneWay') || 'Arrival';
            case TransferDirectionEnum.DEPARTURE:
                return t('common.enum.transferDirection.comeBack') || 'Departure';
            default:
                return 'Transfer';
        }
    }, [item.direction, t]);

    // Get transport date
    const transportDate = useMemo(() => {
        if (item.transportDate) {
            return moment(item.transportDate).format("DD/MM/YYYY");
        }
        return '-';
    }, [item.transportDate]);

    const transportTime = useMemo(() => {
        if (item.transportTime) {
            // Si es solo una hora (HH:mm), devolverla directamente
            if (typeof item.transportTime === 'string' && /^\d{2}:\d{2}$/.test(item.transportTime)) {
                return item.transportTime;
            }
            // Si es una fecha completa, parsearla
            return moment(item.transportTime).format("HH:mm");
        }
        return '-';
    }, [item.transportTime]);

    // Get status label based on enum value
    const statusLabel = useMemo(() => {
        if (item.status === TransferStatusEnum.CONFIRMED) {
            return t('common.enum.transferStatus.confirmed');
        } else if (item.status === TransferStatusEnum.CANCELLED) {
            return t('common.enum.transferStatus.cancelled');
        } else {
            // Para 0, 1, 2, 5, 6 → Pendiente
            return t('common.enum.transferStatus.pending');
        }
    }, [item.status, t]);

    return (
        <div className="bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-neutral-800 dark:to-neutral-900 rounded-2xl p-4 border border-neutral-200 dark:border-neutral-700 hover:shadow-lg transition-shadow">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                {/* Image */}
                <div className="flex-shrink-0 w-full sm:w-36">
                    <div className="aspect-w-4 aspect-h-3 rounded-xl overflow-hidden">
                        <NcImage src={item.cityEvent.bannerContentUrl} />
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                        <span className="text-sm font-semibold text-primary-700 dark:text-primary-400 uppercase">
                            {directionLabel}
                        </span>
                    </div>

                    {(item.transportLocationFrom && item.transportLocationTo) && (
                        <p className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                            {item.transportLocationFrom.trName?.[languageCode]} → {item.transportLocationTo.trName?.[languageCode]}
                        </p>
                    )}

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-3">
                        <div>
                            <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                                {getTransportDateLabel(t, item?.transport, item?.direction === TransferDirectionEnum.ARRIVAL ? 'arrival' : 'departure')}
                            </p>
                            <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{transportDate}</p>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                                {getTransportTimeLabel(t, item?.transport, item?.direction === TransferDirectionEnum.ARRIVAL ? 'arrival' : 'departure')}
                            </p>
                            <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{transportTime}</p>
                        </div>
                        {item.transferCommonDescription?.supplierVehicle !== undefined && (
                            <div>
                                <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">{t("transfers.fields.vehicle")}</p>
                                <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                                    {getVehicleLabel(t, item.transferCommonDescription.supplierVehicle)}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Info */}
                <div className="flex flex-col sm:items-end gap-3 min-w-fit">
                    {item.status === TransferStatusEnum.CONFIRMED && (
                        <div className="flex flex-col items-start sm:items-end">
                            <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">{t("transfers.fields.confirmationNumber")}</p>
                            <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                                {item?.transferCommonDescription?.id ? `#${item.transferCommonDescription.id}` : t("transfers.message.toConfirm")}
                            </p>
                        </div>
                    )}

                    <Badge name={statusLabel} color={statusColor}/>

                    {item.status !== TransferStatusEnum.CANCELLED && item.transferCommonDescription?.lovePrice && item.passengerDepartment?.name?.toLowerCase() === "individuals" && (
                        <div className="pt-2 border-t border-neutral-200 dark:border-neutral-700 flex flex-col sm:items-end">
                            <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">{t("transfers.fields.price")}</p>
                            <p className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
                                {item.transferCommonDescription?.lovePrice && item.transferCommonDescription.currency.symbol ? (
                                    <>
                                        {formatNumber(item.transferCommonDescription.lovePrice)} <span dangerouslySetInnerHTML={{ __html: item.transferCommonDescription.currency.symbol}} />
                                    </>
                                ) : (
                                    t("transfers.message.toConfirm")
                                )}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default TransferItem;
